import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { getFixtureListQueryKey } from "~pages/Fixtures/FixtureListPage/hooks";
import type { FixtureTransformed } from "~pages/Fixtures/FixtureListPage/types";
import { useRecalculateSeries } from "~pages/Fixtures/hooks";
import type { FixtureResponse } from "~types/fixture";
import { transformFixtureListResponse } from "../../utilities";
import type { EditFixtureForm, FixtureEntityUpsert, FixtureEntrantTransformed, FixturePersonUpsert, FixtureUpsert } from "../types";
import { getScoreSubmitData } from "../utilities";
import { getGetFixtureQueryKey, useGetFixture } from "./useGetFixture";
import { useUpdateFixture } from "./useUpdateFixture";
import { useUpdateFixtureEntity } from "./useUpdateFixtureEntity";
import { useUpdateFixturePerson } from "./useUpdateFixturePerson";

/** Single hook to gather all form data and send it to server on submission. No UI logic, use `onSettled` callback for this. */
export const useEditFixtureFormSubmit = (onSettled?: (results: PromiseSettledResult<unknown>[]) => void) => {
  const { data: fixtureData } = useGetFixture();
  const { mutateAsync: updateFixture } = useUpdateFixture();
  const { mutateAsync: updateEntity } = useUpdateFixtureEntity();
  const { mutateAsync: updatePerson } = useUpdateFixturePerson();
  const { mutate: recalculateSeries } = useRecalculateSeries();

  const { hasPermission } = useUserPermissions();
  const editFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const editParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.UPDATE);
  const editCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.UPDATE);

  const { sport = "", organizationId = "", fixtureId = "", seasonId = "" } = useParams();
  const queryClient = useQueryClient();

  return (values: EditFixtureForm) => {
    const fixtureValues: FixtureUpsert = {
      attendance: values.attendance ? +values.attendance : null,
      externalId: values.externalId || null,
      fixtureNumber: values.fixtureNumber ? +values.fixtureNumber : null,
      internationalReference: values.internationalReference || null,
      includeInStandings: values.includeInStandings,
      liveDataAvailable: values.liveDataAvailable,
      nameLatin: values.nameLatin || null,
      nameLocal: values.nameLocal || null,
      placingIfLost: values.placingIfLost ? +values.placingIfLost : null,
      placingIfWon: values.placingIfWon ? +values.placingIfWon : null,
      poolCode: values.poolCode?.value || null,
      profileId: values.profileId?.value || null,
      sellout: values.sellout,
      seriesCode: values.seriesCode?.value || null,
      stageCode: values.stageCode?.value || null,
      status: values.status,
      ticketURL: values.ticketURL || null,
      timesUnconfirmed: values.timesUnconfirmed,
    };

    const { awayResultPlace, awayResultSecondaryScorePlace, draw, homeResultPlace, homeResultSecondaryScorePlace } = getScoreSubmitData({
      awayScore: values.awayScore,
      awaySecondaryScore: values.awaySecondaryScore,
      homeScore: values.homeScore,
      homeSecondaryScore: values.homeSecondaryScore,
    });

    const getEntrantCommonPayload = (entrant: FixtureEntrantTransformed) => ({
      draw,
      resultPlace: entrant.isHome ? homeResultPlace : awayResultPlace,
      includeInRepresentation: entrant.includeInRepresentation,
      resultSecondaryScorePlace: entrant.isHome ? homeResultSecondaryScorePlace : awayResultSecondaryScorePlace,
      score: (entrant.isHome ? values.homeScore : values.awayScore) || null,
      secondaryScore:
        (values.homeScore === values.awayScore &&
          values.awayScore !== "" &&
          (entrant.isHome ? values.homeSecondaryScore : values.awaySecondaryScore)) ||
        null,
      isNeutralVenue: values.isNeutralVenue,
      externalId: entrant.externalId || null,
      resultStatus: entrant.resultStatus,
      uniformId: entrant.uniform?.value || null,
    });

    const participantValues: FixturePersonUpsert[] = (values.participants ?? []).map((participant) => ({
      ...getEntrantCommonPayload(participant),
      personId: participant.personId,
    }));

    const competitorValues: FixtureEntityUpsert[] = (values.competitors ?? []).map((entity) => ({
      ...getEntrantCommonPayload(entity),
      entityId: entity.entityId,
      includeInConferenceStatistics: values.includeInConferenceStatistics,
    }));

    const oldSeriesCode = fixtureData?.formData.seriesCode?.value;
    const newSeriesCode = values.seriesCode?.value;
    const isSeriesCodeChanged = oldSeriesCode !== newSeriesCode;

    Promise.allSettled([
      ...(editFixtureAllowed ? [updateFixture(fixtureValues)] : []),
      ...(editParticipantsAllowed ? participantValues.map((payload) => updatePerson(payload)) : []),
      ...(editCompetitorsAllowed ? competitorValues.map((payload) => updateEntity(payload)) : []),
    ]).then((results) => {
      const isFixtureSucceeded = results[0].status === "fulfilled";

      if (isFixtureSucceeded && isSeriesCodeChanged) {
        oldSeriesCode && recalculateSeries(oldSeriesCode);
        newSeriesCode && recalculateSeries(newSeriesCode);
      }

      queryClient.invalidateQueries({
        queryKey: getGetFixtureQueryKey({
          sport,
          organizationId,
          fixtureId,
          seasonId,
        }),
      });

      queryClient.setQueryData(getFixtureListQueryKey({ sport, organizationId, seasonId }), (oldFixtures?: FixtureTransformed[]) => {
        if (!oldFixtures) return oldFixtures;

        return oldFixtures.map((oldFixture) => {
          if (oldFixture.fixtureId !== fixtureId) return oldFixture;

          const fixturePromiseResult = (results[0] as PromiseFulfilledResult<FixtureResponse>).value;
          const responseFixture: FixtureTransformed = transformFixtureListResponse(fixturePromiseResult)[0];

          return responseFixture;
        });
      });

      onSettled?.(results);
    });
  };
};
